import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ShieldAlert, CheckCircle, Flag } from 'lucide-react';
import { MathCaptcha } from './MathCaptcha';

const CATEGORIES = [
  { id: 'extortion', label: 'Someone is demanding money for information' },
  { id: 'harassment', label: 'I am receiving harassing or repeated calls' },
  { id: 'fake_sighting', label: 'A sighting on this case looks fake' },
  { id: 'spam', label: 'Spam / promotional contact' },
  { id: 'other', label: 'Other concern' },
];

export default function ReportAbuseDialog({ open, onOpenChange, caseId = null, sightingId = null, token = null, trigger = null }) {
  const [category, setCategory] = useState('extortion');
  const [description, setDescription] = useState('');
  const [suspectPhone, setSuspectPhone] = useState('');
  const [suspectEmail, setSuspectEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const reset = () => {
    setCategory('extortion');
    setDescription('');
    setSuspectPhone('');
    setSuspectEmail('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
    setCaptchaVerified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10) {
      setError('Please describe what happened (at least 10 characters).');
      return;
    }
    if (!captchaVerified) {
      setError('Please complete the security check before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/abuse-reports`, {
        case_id: caseId,
        sighting_id: sightingId,
        category,
        description: description.trim(),
        suspect_phone: suspectPhone.trim() || null,
        suspect_email: suspectEmail.trim() || null,
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      {trigger}
      <DialogContent className="max-w-lg" data-testid="report-abuse-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-2">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <DialogTitle>Report Abuse or Scam</DialogTitle>
          <DialogDescription>
            Tell us what happened. We&apos;ll investigate and may permanently ban the offender.
            <strong className="block mt-2 text-red-700">
              MISYOU advises you — Don&apos;t give any money to anyone without involving your local police station. Always keep your local police informed.
            </strong>
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-6" data-testid="abuse-submitted">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1">Report submitted</h3>
            <p className="text-sm text-slate-600">
              Our moderation team will review it shortly. If this was urgent, please also file a complaint
              at your local police station immediately.
            </p>
            <Button className="mt-5" onClick={() => onOpenChange(false)} data-testid="abuse-close-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="abuse-form">
            <div>
              <Label className="mb-2 block">What happened?</Label>
              <div className="space-y-2">
                {CATEGORIES.map((c) => (
                  <label key={c.id} className="flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-slate-50">
                    <input
                      type="radio"
                      name="category"
                      value={c.id}
                      checked={category === c.id}
                      onChange={(e) => setCategory(e.target.value)}
                      className="mt-1"
                      data-testid={`abuse-cat-${c.id}`}
                    />
                    <span className="text-sm text-slate-700">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="abuse-suspect-phone">Suspect&apos;s phone number (optional)</Label>
              <Input
                id="abuse-suspect-phone"
                value={suspectPhone}
                onChange={(e) => setSuspectPhone(e.target.value)}
                placeholder="+91-XXXXXXXXXX"
                data-testid="abuse-suspect-phone-input"
              />
              <p className="text-xs text-slate-500 mt-1">Helps us spot repeat offenders.</p>
            </div>

            <div>
              <Label htmlFor="abuse-suspect-email">Suspect&apos;s email (optional)</Label>
              <Input
                id="abuse-suspect-email"
                type="email"
                value={suspectEmail}
                onChange={(e) => setSuspectEmail(e.target.value)}
                placeholder="scammer@example.com"
                data-testid="abuse-suspect-email-input"
              />
            </div>

            <div>
              <Label htmlFor="abuse-description">What happened (required)</Label>
              <Textarea
                id="abuse-description"
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="They called demanding ₹50,000 in exchange for information about my missing son..."
                required
                data-testid="abuse-description-input"
              />
            </div>

            {error && <p className="text-sm text-red-600" data-testid="abuse-error">{error}</p>}

            {/* Math CAPTCHA — anti-spam */}
            <MathCaptcha onVerify={setCaptchaVerified} />

            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700"
              size="lg"
              disabled={submitting || !captchaVerified}
              data-testid="abuse-submit-btn"
            >
              <Flag className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting…' : 'Submit Abuse Report'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
